import React from "react";

function TermsOfUse() {
  const sections = [
    {
      title: "1. Agreement to Terms",
      body: [
        `These Terms of Use ("Terms") form a legally binding agreement between you ("User," "you," or "your") and CarFido ("CarFido," "Company," "we," "us," or "our"). By accessing or using the Platform, including via WhatsApp, web interfaces, or any related services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must not use the Platform.`,
      ],
    },
    {
      title: "2. Eligibility",
      body: [
        "You must be at least 13 years old to use the Platform.",
        "If you are under 18, you may only use the Platform with the supervision and consent of a parent or legal guardian who agrees to be bound by these Terms.",
      ],
    },
    {
      title: "3. Nature of the Service",
      body: [
        "CarFido is an AI-powered automotive assistant that provides general informational guidance related to vehicle diagnostics, maintenance, and ownership.",
        "CarFido is not a licensed mechanic or professional service provider.",
        "All outputs are informational and educational in nature only.",
      ],
    },
    {
      title: "4. AI Limitations & No Guarantee",
      body: [
        "CarFido uses artificial intelligence models to generate outputs.",
        "These outputs may be inaccurate, incomplete, or outdated and may not reflect real-world vehicle conditions.",
        "The Company does not guarantee the accuracy, reliability, or completeness of any output.",
      ],
    },
    {
      title: "5. No Professional Advice",
      body: [
        "Information provided by CarFido does not constitute professional mechanical advice and should not be relied upon as a substitute for inspection or diagnosis by a qualified professional.",
      ],
    },
    {
      title: "6. Assumption of Risk",
      body: [
        "You acknowledge that interactions with the Platform may be reviewed for quality assurance, safety monitoring, and product improvement purposes.",
        "You agree that any actions taken based on Dash’s outputs are at your own risk.",
        "You are solely responsible for verifying recommendations before acting on them.",
      ],
    },
    {
      title: "7. Critical Safety Disclaimer",
      body: [
        "Vehicle maintenance and repair involve inherent risks.",
        "You agree not to rely on CarFido for safety-critical systems including brakes, steering, airbags, fuel systems, or any system affecting safe vehicle operation.",
      ],
    },
    {
      title: "8. OBD & Vehicle Data Disclaimer",
      body: [
        "If CarFido integrates with vehicle systems, including OBD data, the Company is not responsible for inaccuracies in sensor data, hardware malfunctions, or resulting diagnostic errors.",
      ],
    },
    {
      title: "9. User Content, Generated Content & Feedback",
      body: [
        `"User Content" means any data you provide, including vehicle details, messages, and inputs.`,
        `"Generated Content" means outputs produced by CarFido.`,
        `"Feedback" means suggestions or ideas you provide.`,
        "You retain ownership of User Content.",
        "You grant the Company a worldwide, non-exclusive, royalty-free license to use, process, store, and analyze User Content to operate, improve, and maintain the Platform.",
        "You may use Generated Content for personal purposes. The Company does not guarantee that Generated Content is unique or protectable.",
        "Feedback is non-confidential and may be used by the Company without restriction.",
      ],
    },
    {
      title: "10. Model Training & Data Use",
      body: [
        "We do not use your personal data, vehicle-specific data, or conversation data to train or fine-tune general-purpose AI models without your explicit consent.",
        "We may use user interactions and generated outputs to improve the performance, accuracy, and safety of the Platform, including through internal evaluation, system tuning, prompt refinement, and development of product features.",
        "Where feasible, such improvements may be performed using anonymized or aggregated data.",
      ],
    },
    {
      title: "11. Acceptable Use",
      body: [
        "You agree not to misuse the Platform, including attempting to reverse engineer, interfere with operations, or use the Platform for unlawful purposes.",
      ],
    },
    {
      title: "12. Moderation & Enforcement",
      body: [
        "We may remove or disable access to any content that violates these Terms or applicable law.",
        "We may suspend or terminate access to the Platform.",
      ],
    },
    {
      title: "13. Third-Party Services & AI Providers",
      body: [
        "CarFido may rely on third-party service providers, including AI platforms, hosting providers, and messaging platforms.",
        "These providers may process your data under their own terms and privacy policies.",
        "The Company is not responsible for third-party practices.",
      ],
    },
    {
      title: "14. Platform Dependencies",
      body: [
        "CarFido may depend on third-party platforms such as WhatsApp.",
        "We are not responsible for outages, delays, or data handling practices of such platforms.",
      ],
    },
    {
      title: "15. Limitation of Liability",
      body: [
        "To the maximum extent permitted by law, the Company shall not be liable for indirect, incidental, special, consequential, or punitive damages, including vehicle damage, personal injury, or loss of profits.",
        "The Company’s total liability shall not exceed the greater of $100 or the amount paid by you in the past 12 months.",
      ],
    },
    {
      title: "16. Indemnification",
      body: [
        "You agree to indemnify and hold harmless the Company from any claims arising from your use or misuse of the Platform or violation of these Terms.",
      ],
    },
    {
      title: "17. Termination",
      body: [
        "We may suspend or terminate your access to the Platform at any time for any reason, including violation of these Terms.",
      ],
    },
    {
      title: "18. Business Transfers",
      body: [
        "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.",
      ],
    },
    {
      title: "19. Arbitration & Class Action Waiver",
      body: [
        "Disputes shall be resolved through binding arbitration on an individual basis.",
        "You waive any right to participate in class actions.",
      ],
    },
    {
      title: "20. Governing Law",
      body: [
        "These Terms shall be governed by the laws of the State of [Insert State], without regard to conflict of law principles.",
      ],
    },
    {
      title: "21. Modifications & Interruptions",
      body: [
        "We may modify or discontinue the Platform at any time without liability.",
      ],
    },
    {
      title: "22. Changes to Terms",
      body: [
        "We may update these Terms from time to time. Continued use constitutes acceptance of the updated Terms.",
      ],
    },
  ];

  const styles = {
    page: {
      minHeight: "100vh",
      margin: 0,
      background: "#F9F9F9",
      color: "#414141",
      fontFamily: "Nunito, Inter, sans-serif",
      lineHeight: 1.7,
    },
    hero: {
      background: "linear-gradient(160deg, #1F1F1F 0%, #2A2A2A 60%, #414141 100%)",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      color: "#ffffff",
    },
    heroInner: {
      maxWidth: "1120px",
      margin: "0 auto",
      padding: "80px 24px 60px",
    },
    eyebrow: {
      margin: "0 0 12px",
      color: "#D81F26",
      fontSize: "12px",
      fontWeight: 800,
      letterSpacing: "0.25em",
      textTransform: "uppercase",
    },
    h1: {
      margin: "0 0 16px",
      color: "#ffffff",
      fontSize: "44px",
      lineHeight: 1.15,
      fontWeight: 900,
      maxWidth: "820px",
      letterSpacing: "-0.02em",
    },
    intro: {
      maxWidth: "760px",
      margin: 0,
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "17px",
      fontWeight: 500,
    },
    meta: {
      marginTop: "20px",
      color: "rgba(255, 255, 255, 0.5)",
      fontSize: "13px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    container: {
      maxWidth: "1120px",
      margin: "0 auto",
      padding: "48px 24px 80px",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 300px",
      gap: "36px",
      alignItems: "start",
    },
    main: {
      maxWidth: "780px",
      minWidth: 0,
    },
    section: {
      padding: "30px 32px",
      marginBottom: "24px",
      border: "1px solid #E6E6E6",
      borderRadius: "24px",
      background: "rgba(255, 255, 255, 0.6)",
      backdropFilter: "blur(8px)",
      boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
    },
    h2: {
      margin: "0 0 16px",
      color: "#414141",
      fontSize: "20px",
      lineHeight: 1.3,
      fontWeight: 800,
      letterSpacing: "-0.01em",
    },
    p: {
      margin: 0,
      color: "#414141",
      fontSize: "15px",
      lineHeight: 1.75,
      fontWeight: 500,
    },
    ul: {
      margin: 0,
      paddingLeft: "20px",
      color: "#414141",
      fontSize: "15px",
      lineHeight: 1.75,
      fontWeight: 500,
    },
    li: {
      marginBottom: "10px",
    },
    aside: {
      position: "sticky",
      top: "100px",
    },
    sideCard: {
      border: "1px solid #E6E6E6",
      borderRadius: "24px",
      background: "#ffffff",
      padding: "24px",
      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
    },
    sideTitle: {
      margin: "0 0 12px",
      color: "#414141",
      fontSize: "16px",
      fontWeight: 800,
      letterSpacing: "-0.01em",
    },
    sideText: {
      margin: "0 0 16px",
      color: "#414141",
      fontSize: "13.5px",
      lineHeight: 1.6,
      fontWeight: 500,
    },
    contactButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: "12px 20px",
      borderRadius: "9999px",
      background: "#000000",
      color: "#ffffff",
      textDecoration: "none",
      fontSize: "11px",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: "0.15em",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease-in-out",
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }
        a:hover { color: #D81F26 !important; }
        .terms-contact-btn:hover { background: #414141 !important; color: #ffffff !important; transform: scale(1.02); }
        .terms-contact-btn:active { transform: scale(0.95); }
        section:last-child { margin-bottom: 0 !important; }

        @media (max-width: 860px) {
          .terms-layout { grid-template-columns: 1fr !important; }
          .terms-aside { position: static !important; }
          .terms-container { gap: 24px !important; }
        }

        @media (max-width: 640px) {
          .terms-hero { padding: 50px 16px 40px !important; }
          .terms-title { font-size: 32px !important; line-height: 1.2 !important; }
          .terms-container { padding: 26px 16px 56px !important; }
          .terms-container section {
            padding: 22px 20px !important;
            border-radius: 20px !important;
          }
          .terms-container section h2 { font-size: 18px !important; }
          .terms-container section p,
          .terms-container section li { font-size: 14.5px !important; }
        }
      `}</style>

      <section style={styles.hero}>
        <div className="terms-hero" style={styles.heroInner}>
          <p style={styles.eyebrow}>Terms of Use & Disclaimer</p>

          <h1 className="terms-title" style={styles.h1}>
            CarFido Terms of Use
          </h1>

          <p style={styles.intro}>
            These Terms explain the rules, limitations, disclaimers, and user
            responsibilities that apply when using CarFido.
          </p>

          <div style={styles.meta}>
            Effective Date: May 18, 2026
          </div>
        </div>
      </section>

      <div className="terms-container terms-layout" style={styles.container}>
        <main style={styles.main}>
          {sections.map((section) => (
            <section key={section.title} style={styles.section}>
              <h2 style={styles.h2}>{section.title}</h2>

              {section.body.length === 1 ? (
                <p style={styles.p}>{section.body[0]}</p>
              ) : (
                <ul style={styles.ul}>
                  {section.body.map((item) => (
                    <li key={item} style={styles.li}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </main>

        <aside className="terms-aside" style={styles.aside}>
          <div style={styles.sideCard}>
            <h2 style={styles.sideTitle}>Important Disclaimer</h2>

            <p style={styles.sideText}>
              CarFido provides informational automotive guidance only. It is
              not a substitute for inspection, diagnosis, or repair by a
              qualified professional.
            </p>

            <a href="/contact" className="terms-contact-btn" style={styles.contactButton}>
              Contact CarFido
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default TermsOfUse;
